import { useState, useRef, useEffect, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { isJobPostExpired } from '../../utils';
import { JobPost, RawNotification } from '../../types';
import { readState, persistState, JobPostMatchState } from './utils/localStorage';
import { isJobPostMatchMessage, hasRequiredFields } from './utils/validations';
import { parseNotification } from './utils/notificationParser';

const defaultInitialState: JobPostMatchState = {
    jobPost: {},
    isOpen: false,
};

const readInitialState = (): JobPostMatchState => {
    const state = readState();

    if (isEmpty(state) || isJobPostExpired(state.jobPost as JobPost)) {
        return defaultInitialState;
    }

    return state as JobPostMatchState;
};

export interface JobPostMatchController {
    close: () => void;
    onNewNotification: (notification: RawNotification) => void;
    jobPost: JobPost;
    isOpen: boolean;
}

const useJobPostMatchController = (): JobPostMatchController => {
    const [initialState] = useState(() => readInitialState());

    const [jobPost, setJobPost] = useState<JobPost>(initialState.jobPost);

    const [isOpen, setIsOpen] = useState(initialState.isOpen);

    const firstRender = useRef(true);
    useEffect(() => {
        if (!firstRender.current) {
            persistState({ jobPost, isOpen } as JobPostMatchState);
        }

        firstRender.current = false;
    }, [jobPost, isOpen]);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    const onNewNotification = (notification: RawNotification) => {
        const parsedNotification = parseNotification(notification);

        if (!shouldHandleNotification(parsedNotification)) {
            return;
        }

        const { isOpen: storageIsOpen } = readState();
        if (!isOpen || !storageIsOpen) {
            return updateNewJobPost(parsedNotification);
        }
    };

    const updateNewJobPost = (parsedNotification: ReturnType<typeof parseNotification>) => {
        setJobPost(parsedNotification);
        setIsOpen(true);
    };

    const shouldHandleNotification = (notification: ReturnType<typeof parseNotification>) =>
        isJobPostMatchMessage(notification) && hasRequiredFields(notification) && !isJobPostExpired(notification);

    return {
        close,
        onNewNotification,
        jobPost,
        isOpen,
    };
};

export default useJobPostMatchController;
